/**
 * PHOTO GEO-STAMP (proof-of-presence) — PURE logic, with no DOM or browser.
 *
 * A photo's compliance value increases greatly if it also proves
 * WHERE it was taken. Everything that does not need `navigator` lives here
 * (the actual coordinate capture happens in the web layer); this layer
 * validates, normalizes, and formats the stamp for verifiable offline
 * display and export.
 */

/** Geographic stamp of an attachment (everything optional: no location permission → no stamp). */
export interface GeoStamp {
  latitude: number;
  longitude: number;
  /** Accuracy radius in meters reported by the device (optional). */
  accuracy?: number | null;
}

/** Loose shape of an `attachment` row regarding the stamp (nullable because of SQLite). */
export interface GeoStampSource {
  latitude?: number | null;
  longitude?: number | null;
  geo_accuracy?: number | null;
}

/**
 * Is `(lat, lng)` a valid geographic coordinate? Rejects NaN/Infinity,
 * out-of-range values, and the exact origin `(0, 0)` (Null Island: almost always
 * a GPS without a fix, not a real inspection location).
 */
export function isValidCoordinate(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return false;
  }
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return false;
  }
  // Null Island: (0,0) is the fallback for a failed fix, not a real inspection.
  if (latitude === 0 && longitude === 0) {
    return false;
  }
  return true;
}

/**
 * Extracts a `GeoStamp` from an attachment row, or `null` if it has no valid
 * stamp. Normalizes `accuracy` to a finite non-negative value (or `null`).
 */
export function readGeoStamp(source: GeoStampSource | null | undefined): GeoStamp | null {
  if (!source) {
    return null;
  }
  const { latitude, longitude } = source;
  // Guard with `typeof` (in addition to semantic validation) so TS narrows
  // `number | null | undefined` to `number` without assertions.
  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return null;
  }
  if (!isValidCoordinate(latitude, longitude)) {
    return null;
  }
  const rawAcc = source.geo_accuracy;
  const accuracy =
    typeof rawAcc === "number" && Number.isFinite(rawAcc) && rawAcc >= 0 ? rawAcc : null;
  return { latitude, longitude, accuracy };
}

/** Does the attachment row have a valid geo-stamp? */
export function hasGeoStamp(source: GeoStampSource | null | undefined): boolean {
  return readGeoStamp(source) !== null;
}

/**
 * Formats a coordinate as a signed decimal with `precision` decimals
 * (default 5 ≈ 1 m). Rounds stably (without `-0`).
 */
export function formatCoordinate(value: number, precision = 5): string {
  const rounded = Number(value.toFixed(precision));
  const normalized = Object.is(rounded, -0) ? 0 : rounded;
  return normalized.toFixed(precision);
}

/**
 * Readable stamp label: `"12.34560, -76.54320"`, or with a hemisphere suffix
 * when `hemisphere` is true: `"12.34560° N, 76.54320° W"`.
 */
export function formatGeoStamp(
  stamp: GeoStamp,
  options: { precision?: number; hemisphere?: boolean } = {},
): string {
  const precision = options.precision ?? 5;
  if (options.hemisphere) {
    const lat = `${formatCoordinate(Math.abs(stamp.latitude), precision)}° ${
      stamp.latitude >= 0 ? "N" : "S"
    }`;
    const lng = `${formatCoordinate(Math.abs(stamp.longitude), precision)}° ${
      stamp.longitude >= 0 ? "E" : "W"
    }`;
    return `${lat}, ${lng}`;
  }
  return `${formatCoordinate(stamp.latitude, precision)}, ${formatCoordinate(
    stamp.longitude,
    precision,
  )}`;
}

/**
 * Formats the fix's accuracy as `"±12 m"` / `"±1.3 km"`, or `null` if absent.
 */
export function formatAccuracy(accuracy: number | null | undefined): string | null {
  if (typeof accuracy !== "number" || !Number.isFinite(accuracy) || accuracy < 0) {
    return null;
  }
  if (accuracy >= 1000) {
    const km = accuracy / 1000;
    // One decimal below 10 km, integer above.
    return `±${km >= 10 ? Math.round(km) : Number(km.toFixed(1))} km`;
  }
  return `±${Math.round(accuracy)} m`;
}

/**
 * URL to a map (OpenStreetMap, with no API key or third-party tracking) centered on
 * the stamp. Verifiable offline in the sense that the coordinate is in the URL.
 */
export function geoMapUrl(stamp: GeoStamp, zoom = 16): string {
  const lat = formatCoordinate(stamp.latitude, 6);
  const lng = formatCoordinate(stamp.longitude, 6);
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`;
}
